import { Link, NavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useFilmmakerScope } from "../context/FilmmakerScope";
import { clearFilmmakerScope } from "../hooks/useFilmmaker";
import { LogOut, UserCircle2, ShieldCheck, X } from "lucide-react";

export default function MainHeader() {
  const { user, logout } = useAuth();
  const scope = useFilmmakerScope();
  return (
    <header
      data-testid="main-header"
      className="sticky top-0 z-40 backdrop-blur-xl bg-gs-cream/80 border-b border-gs-line"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
        {scope.isFilmmakerMode && scope.filmmaker ? (
          <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group">
            <img src="/brand/monogram.png" alt="Green Screen monogram" className="h-9 md:h-10 w-auto opacity-80" />
            <div className="leading-none">
              <p className="font-display text-2xl md:text-[26px] tracking-wide text-gs-ink leading-none">{scope.filmmaker.name.toUpperCase()}</p>
              <p className="font-mono text-[9px] uppercase tracking-[0.45em] text-gs-dust mt-1">on green screen pictures</p>
            </div>
          </Link>
        ) : (
          <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group">
            <img src="/brand/monogram.png" alt="Green Screen monogram" className="h-11 md:h-12 w-auto" />
            <div className="hidden sm:block leading-none">
              <p className="font-display text-2xl md:text-[26px] tracking-wide text-gs-ink leading-none">GREEN SCREEN</p>
              <p className="font-mono text-[9px] uppercase tracking-[0.45em] text-gs-dust mt-1">pictures</p>
            </div>
          </Link>
        )}

        <nav className="hidden md:flex items-center gap-8 font-sans text-sm text-gs-ink/80">
          <NavLink data-testid="nav-now-showing" to="/" end className={({ isActive }) => isActive ? "text-gs-ember" : "hover:text-gs-ember transition-colors"}>
            Now Showing
          </NavLink>
          <NavLink data-testid="nav-coming-soon" to="/#coming-soon" className="hover:text-gs-ember transition-colors">
            Coming Soon
          </NavLink>
          <NavLink data-testid="nav-box-office" to="/box-office" className={({ isActive }) => isActive ? "text-gs-ember" : "hover:text-gs-ember transition-colors"}>
            Box Office
          </NavLink>
          <NavLink data-testid="nav-about" to="/about" className={({ isActive }) => isActive ? "text-gs-ember" : "hover:text-gs-ember transition-colors"}>
            The Director
          </NavLink>
          {user && user.role === "admin" && (
            <NavLink data-testid="nav-admin" to="/admin" className="inline-flex items-center gap-1 text-gs-ember">
              <ShieldCheck size={14} /> Admin
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-3">
          {scope.isFilmmakerMode && (
            <button
              data-testid="exit-filmmaker-scope"
              onClick={() => { clearFilmmakerScope(); window.location.href = "/"; }}
              className="hidden md:inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-gs-dust hover:text-gs-ink"
              title="Exit filmmaker subdomain preview"
            >
              <X size={12} /> Back to Green Screen
            </button>
          )}
          {user && user.id ? (
            <>
              <Link
                to="/library"
                data-testid="nav-library"
                className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-gs-ink hover:text-gs-ember"
              >
                <UserCircle2 size={16} /> {user.name?.split(" ")[0] || "Library"}
              </Link>
              <button
                data-testid="logout-btn"
                onClick={logout}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-gs-dust hover:text-gs-ink"
              >
                <LogOut size={14} /> Sign out
              </button>
            </>
          ) : (
            <>
              <Link
                data-testid="nav-login"
                to="/login"
                className="text-sm font-medium text-gs-ink hover:text-gs-ember"
              >
                Sign in
              </Link>
              <Link
                data-testid="nav-register"
                to="/register"
                className="bg-gs-ink text-gs-cream rounded-full px-5 py-2.5 text-xs font-mono uppercase tracking-[0.25em] border border-gs-ember/50 hover:bg-gs-ember hover:text-gs-ink transition-all"
              >
                Get a Ticket
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
